// One place for "what does this lock fit". The chooser, the category filters and the
// chooser audit test all read these, so a lock cannot sit in one section on the hub and
// a different one in the chooser. CommonJS, no spread/destructuring (CRA content gotcha).
const CHOOSER_STATUSES = ['sale', 'install-only'];

const isDigitalLock = (p) => (p.categories || []).indexOf('digital-door-locks') !== -1;
// 'quote' and 'coming-soon' locks are listed in their lanes but never recommended.
const isOrderable = (p) => CHOOSER_STATUSES.indexOf(p.saleStatus || 'sale') !== -1;
const isSliding = (p) => !!(p.fit && p.fit.sliding);
const hasFingerprint = (p) => (p.unlock || []).some((u) => /fingerprint/i.test(u));
// Apple Home / Google Home / Alexa: the "from my phone" answer.
const hasEcosystem = (p) => (p.ecosystems || []).length > 0;
const hasApple = (p) => (p.ecosystems || []).some((e) => /apple/i.test(e));
const hasWifi = (p) => (p.connectivity || []).some((c) => /built in/i.test(c));
// The lock's own app (Bluetooth or Wi-Fi) — the fallback when no ecosystem lock fits the door.
const hasApp = (p) => (p.unlock || []).concat(p.connectivity || []).some((u) => /\bapp\b|wi-fi/i.test(u));
// A straight swap onto a 54 mm round-bore door: deadbolts and bore levers.
const isBoreSwap = (p) => !isSliding(p) && (p.categories || []).indexOf('deadbolts-and-levers') !== -1 && !!p.install && p.install.tier === 'swap';
// Fits over the existing cylinder, so the existing lock (and its backset) stays.
const isRetrofit = (p) => !!(p.fit && /retrofit/i.test(p.fit.lockType || ''));
// A lock cut into the door edge (mortice): fit is decided by the backset.
const isMortice = (p) => !isSliding(p) && !isBoreSwap(p) && !isRetrofit(p) && !!p.install && p.install.tier === 'cut';
const backsets = (p) => (p.fit && p.fit.backsetsMm) || [];
// `fit.faceMounted` marks a lock that sits on the door face instead of in a pocket (a retrofit
// over the cylinder, a rim lock): the backset is irrelevant, but the body needs a timber or
// wide door — there is no room on a narrow aluminium stile. An empty backset list without it
// means "not confirmed yet", and such a lock is never a pick for a given door.
const isFaceMounted = (p) => !!(p.fit && p.fit.faceMounted);
const backsetKnown = (p) => backsets(p).length > 0 || isFaceMounted(p);
// 'narrow' = a narrow aluminium stile (30–45 mm); 'standard' = timber or a wide aluminium door (50–70 mm).
const fitsBackset = (p, k) => {
  if (isFaceMounted(p)) return k === 'standard';
  const b = backsets(p);
  return k === 'narrow' ? b.some((x) => x <= 45) : b.some((x) => x >= 50);
};
// Which of the four "what is on the door now" answers a lock can replace.
const fitsOnDoor = (p, onDoor) => {
  if (isSliding(p)) return false;
  // A gripset's deadbolt and a night latch both sit over a round bore: the same swaps apply.
  if (onDoor === 'bore' || onDoor === 'gripset' || onDoor === 'nightlatch') return isBoreSwap(p);
  if (onDoor === 'euro') return isRetrofit(p) || isMortice(p);
  if (onDoor === 'mortice') return isMortice(p) || isRetrofit(p);
  if (onDoor === 'none') return isMortice(p);
  return false; // 'other' never gets a software pick — photos and a locksmith instead
};

module.exports = { isDigitalLock, isOrderable, isSliding, hasFingerprint, hasEcosystem, hasApple, hasWifi, hasApp, isBoreSwap, isRetrofit, isMortice, isFaceMounted, backsets, backsetKnown, fitsBackset, fitsOnDoor };
